import { RefObject, useCallback } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { type Camera, OrthographicCamera, Vector3 } from 'three'
import { useAppStore } from '@stores'
import { useSelectionStore } from '@editorStores'
import {
  floorplanPanTruck,
  floorplanZoomInHotkeys,
  floorplanZoomOutHotkeys,
  floorplanZoomTarget,
  isArrowKeyOwnedByFocusedWidget,
  isPhysicalArrowKey,
} from 'src/hooks/sceneArrowKeys'

type FloorplanControls = {
  truck: (x: number, y: number, enableTransition?: boolean) => Promise<void>
  reset: (enableTransition?: boolean) => Promise<unknown>
  getTarget: (out: Vector3, receiveEndValue?: boolean) => Vector3
  moveTo: (x: number, y: number, z: number, enableTransition?: boolean) => Promise<void>
  zoomTo: (zoom: number, enableTransition?: boolean) => Promise<void>
}

/**
 * Arrow keys truck the orthographic floorplan. Plus and minus zoom it, about
 * the current target. `#` restores the zoom and position from when the
 * floorplan was framed. Disabled while the anchor table is open and while
 * focus is in a table, grid, menu, or other widget that already consumes
 * these keys. Form fields are ignored by react-hotkeys-hook itself.
 */
const useFloorplanArrowPan = (
  controlsRef: RefObject<FloorplanControls | null>,
  cameraRef: RefObject<Camera>,
  isOrthographic: boolean,
  hasFloorplanEntry: RefObject<boolean>,
) => {
  const panStep = useAppStore(state => state.floorplanPanStep)
  const zoomFactor = useAppStore(state => state.floorplanZoomFactor)
  const isAnchorTableOpen = useSelectionStore(state => state.mode === 'tensile-connectors-table')
  const keyboardEnabled = isOrthographic && !isAnchorTableOpen

  const pan = useCallback(
    (event: KeyboardEvent) => {
      const controls = controlsRef.current
      const offset = floorplanPanTruck(event.code, panStep)
      if (!controls || !offset) return
      // Drop any queued step so a held key eases from where the camera is,
      // instead of coasting through every repeat after release.
      const current = controls.getTarget(new Vector3(), false)
      void controls.moveTo(current.x, current.y, current.z, false)
      void controls.truck(offset.x, offset.y, true)
    },
    [controlsRef, panStep],
  )

  const ignoreArrowPan = useCallback(
    (event: KeyboardEvent) => !isPhysicalArrowKey(event) || isArrowKeyOwnedByFocusedWidget(event),
    [],
  )

  const zoomBy = useCallback(
    (direction: 1 | -1) => {
      const controls = controlsRef.current
      const camera = cameraRef.current
      if (!controls || !(camera instanceof OrthographicCamera)) return
      const next = floorplanZoomTarget(camera.zoom, direction, zoomFactor)
      if (next == null) return
      // Drop any queued step so a held key eases from the current zoom,
      // instead of coasting through every repeat after release.
      void controls.zoomTo(camera.zoom, false)
      void controls.zoomTo(next, true)
    },
    [cameraRef, controlsRef, zoomFactor],
  )

  useHotkeys(
    ['arrowleft', 'arrowright', 'arrowup', 'arrowdown'],
    pan,
    {
      enabled: keyboardEnabled,
      preventDefault: true,
      ignoreEventWhen: ignoreArrowPan,
    },
    [pan],
  )

  useHotkeys(
    floorplanZoomInHotkeys,
    () => zoomBy(1),
    {
      enabled: keyboardEnabled,
      preventDefault: true,
      ignoreEventWhen: isArrowKeyOwnedByFocusedWidget,
    },
    [zoomBy],
  )

  useHotkeys(
    floorplanZoomOutHotkeys,
    () => zoomBy(-1),
    {
      enabled: keyboardEnabled,
      preventDefault: true,
      ignoreEventWhen: isArrowKeyOwnedByFocusedWidget,
    },
    [zoomBy],
  )

  const resetView = useCallback(() => {
    if (!hasFloorplanEntry.current) return
    void controlsRef.current?.reset(true)
  }, [controlsRef, hasFloorplanEntry])

  useHotkeys('#', resetView, {
    // `#` is unshifted on a German keyboard and Shift+3 on a US keyboard.
    enabled: keyboardEnabled,
    preventDefault: true,
    ignoreModifiers: true,
    ignoreEventWhen: isArrowKeyOwnedByFocusedWidget,
  })
}

export default useFloorplanArrowPan
